// 配信先プロファイル(設計書 §3.4.3)の React Query フック

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './client';

export type Platform = 'fc2_live' | 'youtube' | 'twitch' | 'custom';

export interface StreamingProfile {
  ulid: string;
  name: string;
  platform: Platform;
  rtmp_url: string;
  stream_key_masked: string;
  is_default: boolean;
  available_for_plan: boolean;
  created_at: string;
}

export const profilesQueryKey = ['streaming-profiles'] as const;

export function useStreamingProfiles() {
  return useQuery({
    queryKey: profilesQueryKey,
    queryFn: () => apiRequest<StreamingProfile[]>('/streaming-profiles'),
  });
}

export interface CreateProfileInput {
  name: string;
  platform: Platform;
  rtmp_url: string;
  stream_key: string;
  is_default?: boolean;
}

export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProfileInput) =>
      apiRequest<StreamingProfile>('/streaming-profiles', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profilesQueryKey });
    },
  });
}

export interface UpdateProfileInput {
  name?: string;
  platform?: Platform;
  rtmp_url?: string;
  stream_key?: string;
}

export function useUpdateProfile(ulid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) =>
      apiRequest<StreamingProfile>(`/streaming-profiles/${ulid}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profilesQueryKey });
    },
  });
}

export function useDeleteProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<void>(`/streaming-profiles/${ulid}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profilesQueryKey });
    },
  });
}

export function useSetDefaultProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ulid: string) =>
      apiRequest<StreamingProfile>(`/streaming-profiles/${ulid}/default`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profilesQueryKey });
    },
  });
}

export const PLATFORM_LABELS: Record<Platform, string> = {
  fc2_live: 'FC2 ライブ',
  youtube: 'YouTube Live',
  twitch: 'Twitch',
  custom: 'カスタム RTMP',
};
